import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { MenuItem, TextField, TextFieldProps } from '@mui/material';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { AmcUploadDataSetCriteria } from '../../../types/Criteria/AmcUploadDataSetCriteria';
import { AmcUploadDataSet } from '../../../types/AmcUploadDataSet';
import ApiService from '../../../ApiService';

const useAmcUploadDataSets = (criteria: AmcUploadDataSetCriteria): UseQueryResult<AmcUploadDataSet[]> => {
    return useQuery<AmcUploadDataSet[]>({
        queryKey: ['api/amc/datasets', criteria],
        queryFn: async () => {
            const response = await ApiService.getAmcUploadDataSets(criteria);

            return response.data;
        },
        enabled: true,
        refetchOnWindowFocus: false,
    });
};

type AmcUploadDataSetFieldProps = Omit<TextFieldProps, 'onChange'> & {
    criteria: AmcUploadDataSetCriteria;
    onChange: (value: AmcUploadDataSet | null) => void;
    displayEmpty?: boolean;
};

const AmcUploadDataSetField: FC<AmcUploadDataSetFieldProps> = ({
    criteria,
    onChange,
    displayEmpty = false,
    ...props
}) => {
    const [dataSets, setDataSets] = useState<AmcUploadDataSet[]>([]);
    const [value, setValue] = useState<string>('');

    const { data } = useAmcUploadDataSets(criteria);

    useEffect(() => {
        const newDataSets = data?.filter(({ name }) => name !== null && name.length > 0) || [];
        setDataSets(newDataSets);

        if (newDataSets.length > 0) {
            setValue(newDataSets[0].dataSetId);
            onChange(newDataSets[0]);
        }
    }, [data]);

    const handleOnChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue: AmcUploadDataSet | null | undefined = dataSets.find(
                ({ dataSet }) => dataSet === event.target.value
            );

            setValue(newValue?.dataSetId || '');
            onChange(newValue || null);
        },
        [dataSets, onChange]
    );

    return (
        <TextField
            label="Data Set"
            select
            required={true}
            size="small"
            {...props}
            value={value}
            onChange={handleOnChange}
            disabled={dataSets.length === 0}
            sx={{
                '& .MuiInputLabel-asterisk': {
                    display: 'none',
                },
            }}
            slotProps={{
                select: { native: false },
            }}
            SelectProps={{
                displayEmpty: displayEmpty,
            }}
        >
            {displayEmpty === true && (
                <MenuItem value="">
                    <em>Select a dataset</em>
                </MenuItem>
            )}
            {dataSets.map((dataSet) => {
                return (
                    <MenuItem key={dataSet.dataSet} value={dataSet.dataSet}>
                        {dataSet.name}
                    </MenuItem>
                );
            })}
        </TextField>
    );
};

export type { AmcUploadDataSetFieldProps };
export { useAmcUploadDataSets };
export default AmcUploadDataSetField;
